import React, {
    useEffect,
    useRef,
    useReducer,
    useState,
    useContext,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import {
    ErrorAlert,
    icons,
    TabContainer,
    Tab,
    ToastContainer,
    Toast,
} from 'smartsuite-ui';
import { DateTimeFormats } from 'brock-date-time';
import uuid from 'uuid/v4';
import download from 'downloadjs';
import { putDataPromise } from 'brock-fetch-utils';
import Widget from './Widget';
import {
    getLegendFormatter,
    replaceInArray,
} from './widgets/utils/dataFormatUtils';
import {
    getActiveWidgets,
    getGridTemplateStyles,
    formatViewConfiguration,
    joinRecordLimit,
    shouldShowDashboardFilter,
    getResponsiveLayoutMode,
    getNumberOfColumnsFromPanelConfig,
    getCustomLabelsFromI18n,
} from './dashboardUtils';
import PageFilters from '../shared/filtering/PageFilters';
import GridPanel from './GridPanel';
import { getUpdatedWidget } from './utils/configurationAndValidationUtils';
import DashboardContext from '../shared/hooks/dashboardContext';
import {
    callData,
    flattenAndFormatRequests,
    getTimeZoneFilterProperties,
    initializeWidgetMetricRequests,
    shouldCallTabData,
    viewDataIsExpired,
} from './utils/metricRequestUtils';
import {
    areTimeAndPrecisionFieldsValid,
    isRequiredFiltersHandled,
} from '../shared/filtering/filtersModalUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import SavedDashboardContainer from './sharingAndSaving/SavedDashboardContainer';
import ManageSavedDashboardsModal from './sharingAndSaving/ManageSavedDashboardsModal';
import ShareModal from './sharingAndSaving/ShareModal';
import buildRollupText from '../shared/filtering/rollupTextUtils';
import {
    buildWidgetFilterState,
    shareDashboardLink,
    getPrintShareInfo,
} from './dashboardLinkUtils';
import dashboardExportTypes from './export/dashboardExportTypes';
import DashboardHeader from './DashboardHeader';
import SsiFooter from '../customizations/smartSort/SsiFooter';
import SsiHeader from '../customizations/smartSort/SsiHeader';
import {
    setActivePanelTabs,
    setDashboardFilters,
    setLoadingPanel,
    setIsViewStateLoading,
    setView,
    setWidgetFilters,
} from './stateManagement/dashboardActions';
import dashboardReducer, {
    getInitialDashboardState,
} from './stateManagement/dashboardReducer';
import { useTick } from '../shared/hooks/dashboardHooks';
import {
    applyPublishedFiltersToWidget,
    buildClearedPublisherFilterOptions,
    buildPublishedFilterOptions,
} from '../shared/filtering/publishFilterUtils';
import {
    downloadToastDisplayTime,
    keyDownEventCodes,
    minWindowWidths,
    responsiveLayouts,
} from '../shared/constants/constants';
import useWindowSize from '../shared/hooks/windowSizeHook';
import EmailModal from './export/EmailModal';
import { metricDataPromise } from './utils/fetchUtils';
import FadeErrorAlert from './FadeErrorAlert';
import { useNetworkError } from '../shared/hooks/useNetworkError';

/* eslint-disable max-statements, complexity */
export default function Dashboard(props) {
    const { viewConfiguration } = props;

    const {
        dashboardLabels,
        exportLabels,
        predicateLabels,
        interpolateString,
    } = useLabelContext();

    const { selectOptions, thresholdValues, uriConstants } =
        useContext(DashboardContext);
    const pageFiltersRef = useRef();
    const panelWidgetsRef = useRef();

    const [state, dispatch] = useReducer(
        dashboardReducer,
        getInitialDashboardState(
            viewConfiguration,
            selectOptions,
            props.dashboardKey,
            props.viewOptions
        )
    );

    const [tick, resetTick] = useTick(
        viewConfiguration.refreshRateMilliseconds
    );

    const { width } = useWindowSize();

    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isShareModalVisible, setIsShareModalVisible] = useState(false);
    const [isManageSavedVisible, setIsManageSavedVisible] = useState(false);
    const [isSavedFilterModified, setIsSavedFilterModified] = useState(false);
    const [downloadsInProgress, setDownloadsInProgress] = useState(0);
    const [selectedResponsiveLayout, setSelectedResponsiveLayout] = useState(
        responsiveLayouts.auto
    );
    const [emailState, setEmailState] = useState({
        errors: [],
        isEmailModalVisible: false,
        numberOfEmails: 0,
        showEmailToast: false,
    });

    const handleError = useNetworkError();

    const widgetKeys = state.widgets.reduce(
        (result, widget, index) => ({ ...result, [widget.id]: index }),
        {}
    );

    function callDataForWidgets({
        widgetIds = state.widgets.map((x) => x.id),
        filterState = {
            timeAndPrecision: state.dashboardTimeAndPrecision,
            filterValues: state.dashboardFilterValues,
            whereClauses: state.dashboardWhereClauses,
            recordLimit: state.dashboardRecordLimit,
        },
        widgets = state.widgets,
        panels = state.panels,
        activePanelTabs = state.activePanelTabs,
        resetAllData = false,
    }) {
        const activeWidgets = getActiveWidgets(
            panels,
            widgets,
            activePanelTabs[viewConfiguration.viewKey]
        );
        if (
            isRequiredFiltersHandled(
                viewConfiguration.requiredFilterKeys,
                filterState.filterValues,
                viewConfiguration.availableFilters,
                selectOptions
            ) &&
            areTimeAndPrecisionFieldsValid(
                filterState.timeAndPrecision,
                viewConfiguration.timeAndPrecisionRangeDomains,
                viewConfiguration.showPrecisionConfig,
                interpolateString,
                dashboardLabels
            )
        ) {
            callData(
                state.dashboardKey,
                filterState,
                dispatch,
                uriConstants.metricDataUri,
                viewConfiguration.viewKey,
                activeWidgets.filter((x) => widgetIds.includes(x.id)),
                state.lastViewedTimestamps,
                handleError,
                resetAllData
            );
        }
    }

    useEffect(() => {
        const formattedView = formatViewConfiguration(
            viewConfiguration,
            selectOptions
        );
        setView(dispatch, formattedView);
        if (
            (viewConfiguration.viewKey !== state.viewKey ||
                viewConfiguration.key) !== state.key
        ) {
            setIsSavedFilterModified(false);
        }
        if (
            viewDataIsExpired(state.lastViewedTimestamps, viewConfiguration) ||
            viewConfiguration.viewKey === state.viewKey
        ) {
            callDataForWidgets({
                widgetIds: viewConfiguration.widgets.map((x) => x.id),
                filterState: {
                    timeAndPrecision: formattedView.dashboardTimeAndPrecision,
                    filterValues: formattedView.dashboardFilterValues,
                    whereClauses: formattedView.dashboardWhereClauses,
                    recordLimit: formattedView.dashboardRecordLimit,
                },
                widgets: viewConfiguration.widgets,
                panels: viewConfiguration.panels,
            });
            resetTick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewConfiguration]);

    useEffect(() => {
        if (tick) {
            callDataForWidgets({ widgetIds: state.widgets.map((x) => x.id) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick]);

    function onPublishFilter(publisherFilters, rawDataForSelector) {
        const publishedFilters = buildPublishedFilterOptions(
            publisherFilters,
            rawDataForSelector,
            viewConfiguration.availableFilters,
            selectOptions,
            predicateLabels
        );

        const updatedWidgets = state.widgets.map((widget) =>
            applyPublishedFiltersToWidget(widget, publishedFilters)
        );

        setWidgetFilters(dispatch, updatedWidgets);
        callDataForWidgets({ widgets: updatedWidgets });
    }

    function onRemovePublishFilter(publisherFilters) {
        const publishedFilters = buildClearedPublisherFilterOptions(
            publisherFilters,
            predicateLabels
        );

        const updatedWidgets = state.widgets.map((widget) =>
            applyPublishedFiltersToWidget(widget, publishedFilters)
        );

        setWidgetFilters(dispatch, updatedWidgets);
        callDataForWidgets({ widgets: updatedWidgets });
    }

    function updateDashboardFilterState(
        updatedTimeAndPrecision,
        updatedFilters,
        updatedWhereClauses,
        updatedRecordLimit
    ) {
        setDashboardFilters(
            dispatch,
            updatedTimeAndPrecision,
            updatedFilters,
            updatedWhereClauses,
            updatedRecordLimit
        );

        const updatedConfiguration = {
            timeAndPrecision: updatedTimeAndPrecision,
            filterValues: updatedFilters,
            whereClauses: updatedWhereClauses,
            recordLimit: joinRecordLimit(
                updatedRecordLimit.recordLimit,
                updatedRecordLimit.isDescending
            ),
        };

        callDataForWidgets({
            widgetIds: state.widgets.map((x) => x.id),
            filterState: updatedConfiguration,
            resetAllData: true,
        });
        resetTick();
    }

    function handleDashboardFiltersSave(
        updatedTimeAndPrecision,
        updatedFilters,
        updatedWhereClauses,
        updatedRecordLimit
    ) {
        updateDashboardFilterState(
            updatedTimeAndPrecision,
            updatedFilters,
            updatedWhereClauses,
            updatedRecordLimit
        );
        setIsSavedFilterModified(true);
    }

    function handleWidgetMenuFilter(menuFilters, menuTimeAndPrecision) {
        const updatedFilters = {
            ...state.dashboardFilterValues,
            ...menuFilters,
        };
        const updatedTimeAndPrecision =
            menuTimeAndPrecision ?? state.dashboardTimeAndPrecision;

        return handleDashboardFiltersSave(
            updatedTimeAndPrecision,
            updatedFilters,
            state.dashboardWhereClauses,
            state.dashboardRecordLimit
        );
    }

    function handleWidgetReset(index) {
        return () => {
            const initialWidget = viewConfiguration.widgets[index];
            const initialWidgetState = {
                ...initialWidget,
                metricRequests: initializeWidgetMetricRequests(
                    initialWidget.metricRequests
                ),
            };

            const updatedWidgets = replaceInArray(
                initialWidgetState,
                state.widgets,
                index
            );

            setLoadingPanel(dispatch, initialWidget.panelId);
            setWidgetFilters(dispatch, updatedWidgets);
            callDataForWidgets({ widgets: updatedWidgets });
        };
    }

    function handleWidgetFiltersSave(index) {
        return (
            updatedTimeAndPrecision,
            updatedFilters,
            updatedWhereClauses,
            updatedRecordLimit
        ) => {
            const currentWidget = state.widgets[index];

            const updatedWidget = getUpdatedWidget(
                currentWidget,
                updatedTimeAndPrecision,
                updatedFilters,
                updatedWhereClauses,
                updatedRecordLimit
            );

            const updatedWidgets = replaceInArray(
                updatedWidget,
                state.widgets,
                index
            );

            setLoadingPanel(dispatch, currentWidget.panelId);
            setWidgetFilters(dispatch, updatedWidgets);
            callDataForWidgets({ widgets: updatedWidgets });
        };
    }

    function onSaveDashboard(name, isSaveNew) {
        const {
            updatedTimeAndPrecision,
            updatedFilterValues,
            updatedWhereClauses,
            updatedRecordLimit,
        } = pageFiltersRef.current.getCurrentState();

        const updatedFilterState = {
            timeAndPrecision: updatedTimeAndPrecision,
            filterValues: updatedFilterValues,
            whereClauses: updatedWhereClauses,
            recordLimit: updatedRecordLimit.recordLimit,
            isDescending: !!updatedRecordLimit.isDescending,
        };

        props.saveDashboard(name, isSaveNew, updatedFilterState);
    }

    function onDownload(exportType) {
        const configurationForDownload = {
            timeAndPrecision: state.dashboardTimeAndPrecision,
            filterValues: state.dashboardFilterValues,
            whereClauses: state.dashboardWhereClauses,
            recordLimit: state.dashboardRecordLimit.recordLimit,
            isDescending: !!state.dashboardRecordLimit.isDescending,
        };

        const { id, link } = getPrintShareInfo(
            state.viewKey,
            props.dashboardKey,
            uriConstants.getShareableUri,
            exportType.orientation
        );

        shareDashboardLink(
            id,
            configurationForDownload,
            uriConstants.saveSharedUrl
        ).then(() => window.open(link));
    }

    function onWidgetDownload(downloadData, widgetTitle) {
        setDownloadsInProgress((prevState) => prevState + 1);

        download(
            downloadData,
            `${props.dashboardKey}_${viewConfiguration.viewKey}__${widgetTitle}.csv`,
            'text/csv'
        );

        setTimeout(() => {
            setDownloadsInProgress((prevState) => prevState - 1);
        }, downloadToastDisplayTime);
    }

    function onEmailStart() {
        setEmailState((prevState) => ({
            numberOfEmails:
                prevState.numberOfEmails <= 0
                    ? 1
                    : prevState.numberOfEmails + 1,
            showEmailToast: true,
            errors: [],
        }));
    }

    function onEmailSuccess() {
        setEmailState((prevState) => ({
            ...prevState,
            numberOfEmails: prevState.numberOfEmails - 1,
        }));
    }

    function onEmailError(error) {
        setEmailState((prevState) => ({
            errors: [
                {
                    name: exportLabels.emailFailed,
                    message: error.exceptionMessage || error.statusText,
                },
            ],
            numberOfEmails: prevState.numberOfEmails - 1,
        }));

        setTimeout(() => setEmailState({ errors: [] }), 5000);
    }

    function fileNameForExport() {
        let fileName = viewConfiguration.title;
        if (props.viewOptions.length > 1) {
            const view =
                viewConfiguration.viewLabel ??
                dashboardLabels[viewConfiguration.viewKey] ??
                viewConfiguration.viewKey;

            fileName += `: ${view}`;
        }
        fileName += `_${moment().format(DateTimeFormats.DATETIME_SECONDS)}`;
        return fileName;
    }

    function sendEmail(recipients, orientation) {
        onEmailStart();

        const metricRequests = flattenAndFormatRequests(
            state.widgets,
            state.dashboardTimeAndPrecision,
            state.dashboardFilterValues,
            state.dashboardWhereClauses,
            state.dashboardRecordLimit
        );

        const requestObject = {
            dashboardKey: props.dashboardKey,
            viewKey: viewConfiguration.viewKey,
            metricRequests,
            ...getTimeZoneFilterProperties(
                state.dashboardTimeAndPrecision.timeZoneFiltering,
                state.dashboardFilterValues
            ),
        };

        metricDataPromise(uriConstants.metricDataUri, requestObject)
            .then((metricData) => {
                putDataPromise(uriConstants.pdfExportUri, {
                    orientation,
                    userId: '',
                    recipients,
                    fileName: fileNameForExport(),
                    language: props.siteLanguage,
                    localStorageKey: uuid(),
                    data: JSON.stringify({
                        viewConfiguration: {
                            ...viewConfiguration,
                            timeAndPrecision: state.dashboardTimeAndPrecision,
                            filterValues: state.dashboardFilterValues,
                            whereClauses: state.dashboardWhereClauses,
                            recordLimit: state.dashboardRecordLimit,
                            widgets: state.widgets,
                        },
                        metricData,
                        thresholdValues,
                        customLabels: getCustomLabelsFromI18n(),
                    }),
                })
                    .then(onEmailSuccess)
                    .catch(onEmailError);
            })
            .catch(onEmailError);
    }

    function renderWidgets(panel, panelWidgets, widgetWidth, widgetHeight) {
        return (
            <div
                className="grid-panel__widgets"
                style={panel.bodyStyles}
                ref={panelWidgetsRef}
            >
                {panelWidgets.map(({ index, widget }) => (
                    <Widget
                        // eslint-disable-next-line
                        key={index}
                        availableFilters={viewConfiguration.availableFilters}
                        currentIndex={index}
                        defaultFilterValues={state.dashboardFilterValues}
                        defaultTimeAndPrecision={
                            state.dashboardTimeAndPrecision
                        }
                        defaultWhereClauses={state.dashboardWhereClauses}
                        defaultRecordLimit={state.dashboardRecordLimit}
                        friendlyColumnLabels={
                            viewConfiguration.friendlyColumnLabels
                        }
                        handleReset={handleWidgetReset}
                        handleSave={handleWidgetFiltersSave}
                        handleWidgetMenuFilter={handleWidgetMenuFilter}
                        isLoading={state.loadingPanel === panel.id}
                        metrics={state.metricData}
                        onSelectorClick={onPublishFilter}
                        onSelectorRemoveClick={onRemovePublishFilter}
                        openLinksInNewTab={viewConfiguration.openLinksInNewTab}
                        precisionTypeOptions={
                            viewConfiguration.precisionTypeOptions
                        }
                        predefinedDateRanges={
                            viewConfiguration.predefinedDateRanges
                        }
                        onDownload={onWidgetDownload}
                        timeAndPrecisionTypeOptions={
                            viewConfiguration.timeAndPrecisionTypeOptions
                        }
                        timeAndPrecisionRangeDomains={
                            viewConfiguration.timeAndPrecisionRangeDomains
                        }
                        widgetKeys={widgetKeys}
                        widgetConfiguration={widget}
                        widgetHeight={widgetHeight}
                        widgetWidth={widgetWidth}
                    />
                ))}
            </div>
        );
    }

    function getWidgetDimensions(panel, widgetsToUse) {
        const layoutMode = getResponsiveLayoutMode(
            selectedResponsiveLayout,
            width
        );
        const numberOfColumns = getNumberOfColumnsFromPanelConfig(
            panel,
            layoutMode
        );

        /* eslint-disable indent */
        const widgetWidth = `calc(99% / ${numberOfColumns})`;

        const widgetHeight = `calc(99% / ${Math.ceil(
            widgetsToUse.length / numberOfColumns
        )})`;
        /* eslint-enable indent */

        return { widgetWidth, widgetHeight };
    }

    function getUpdatedPanelTabs(activePanelTabs, panelId, tabId, viewKey) {
        return {
            ...activePanelTabs,
            [viewKey]: {
                ...(activePanelTabs[viewKey] ?? {}),
                [panelId]: tabId,
            },
        };
    }

    function onTabSelect(panelId, tab, tabWidgets) {
        document.activeElement.blur();

        const activePanelTabs = getUpdatedPanelTabs(
            state.activePanelTabs,
            panelId,
            tab.id,
            state.viewKey
        );
        setActivePanelTabs(dispatch, activePanelTabs);

        if (
            shouldCallTabData(
                state.metricData,
                tabWidgets,
                viewConfiguration.refreshRateMilliseconds
            )
        ) {
            setLoadingPanel(dispatch, panelId);
            callDataForWidgets({ activePanelTabs });
        }

        panelWidgetsRef.current.scrollTop = 0;
    }

    function renderTab(tabId, index, panel, tabWidgets) {
        const isActiveTab = state.activePanelTabs[state.viewKey][panel.id]
            ? state.activePanelTabs[state.viewKey][panel.id] === tabId
            : index === 0;

        const tab = panel.tabs.find((x) => x.id?.toString() === tabId);

        if (!tab) {
            throw Error('Panel and Tab id do not match');
        }

        return (
            <Tab
                key={tabId}
                isActive={isActiveTab}
                role="button"
                tabIndex="0"
                onClick={() => onTabSelect(panel.id, tab, tabWidgets)}
                onKeyDown={(e) => {
                    if (keyDownEventCodes.includes(e.keyCode)) {
                        onTabSelect(panel.id, tab, tabWidgets);
                    }
                }}
            >
                {tab.label}
            </Tab>
        );
    }

    function renderPanelTabs(panel, panelWidgets) {
        if (!panel.tabs) {
            const { widgetWidth, widgetHeight } = getWidgetDimensions(
                panel,
                panelWidgets
            );
            return renderWidgets(
                panel,
                panelWidgets,
                widgetWidth,
                widgetHeight
            );
        }

        const groupedPanelWidgets = _.groupBy(
            panelWidgets,
            (x) => x.widget.tabId
        );
        const groupedPanelKeys = Object.keys(groupedPanelWidgets);
        const activePanelTab = state.activePanelTabs[state.viewKey][panel.id];
        const tabWidgets =
            groupedPanelWidgets[activePanelTab || groupedPanelKeys[0]];

        const { widgetWidth, widgetHeight } = getWidgetDimensions(
            panel,
            tabWidgets
        );

        return (
            <div className="grid-panel__tabs">
                <div className="grid-panel__tabs-wrapper">
                    <TabContainer>
                        {groupedPanelKeys.map((tabId, index) =>
                            renderTab(
                                tabId,
                                index,
                                panel,
                                groupedPanelWidgets[tabId]
                            )
                        )}
                    </TabContainer>
                </div>
                {renderWidgets(panel, tabWidgets, widgetWidth, widgetHeight)}
            </div>
        );
    }

    function renderPanels(panels, allWidgets) {
        const showPlaceholder =
            _.isEmpty(state.metricData) || state.areDashboardFiltersLoading;

        return panels.map((panel) => {
            if (showPlaceholder) {
                return (
                    <GridPanel
                        key={panel.id}
                        availableFilters={viewConfiguration.availableFilters}
                        dashboardWidth={width}
                        openLinksInNewTab={viewConfiguration.openLinksInNewTab}
                        panel={panel}
                        selectedResponsiveLayout={selectedResponsiveLayout}
                        showPlaceholder={showPlaceholder}
                    />
                );
            }

            const panelWidgets = allWidgets.reduce((result, widget, index) => {
                if (widget.panelId !== panel.id) {
                    return result;
                }

                result.push({ index, widget });
                return result;
            }, []);

            return (
                <GridPanel
                    key={panel.id}
                    availableFilters={viewConfiguration.availableFilters}
                    dashboardFilterValues={state.dashboardFilterValues}
                    dashboardWidth={width}
                    openLinksInNewTab={viewConfiguration.openLinksInNewTab}
                    panel={panel}
                    panelWidgetsFilterState={panelWidgets.map(({ widget }) =>
                        buildWidgetFilterState(
                            state.dashboardTimeAndPrecision,
                            state.dashboardFilterValues,
                            state.dashboardWhereClauses,
                            widget
                        )
                    )}
                    selectedResponsiveLayout={selectedResponsiveLayout}
                    showPlaceholder={showPlaceholder}
                >
                    {renderPanelTabs(panel, panelWidgets)}
                </GridPanel>
            );
        });
    }

    function renderPageFilters(isRequiredFilters = false) {
        const rollupText = buildRollupText(
            state.dashboardTimeAndPrecision,
            state.dashboardFilterValues,
            state.dashboardWhereClauses,
            viewConfiguration.showTimeAndPrecision,
            viewConfiguration.showPrecisionConfig,
            viewConfiguration.showMeasureFilters,
            viewConfiguration.availableFilters,
            viewConfiguration.requiredFilterKeys,
            selectOptions,
            dashboardLabels
        );

        return (
            <PageFilters
                ref={pageFiltersRef}
                additionalFilterKeys={viewConfiguration.additionalFilterKeys}
                availableFilters={viewConfiguration.availableFilters}
                collapseOnApply={viewConfiguration.collapseOnApply}
                defaultOpen={isRequiredFilters}
                filterValues={state.dashboardFilterValues}
                handleSave={handleDashboardFiltersSave}
                legendFormatter={getLegendFormatter(
                    [],
                    viewConfiguration.friendlyColumnLabels
                )}
                precisionTypeOptions={viewConfiguration.precisionTypeOptions}
                predefinedDateRanges={viewConfiguration.predefinedDateRanges}
                recordLimit={state.dashboardRecordLimit}
                requiredFilterKeys={viewConfiguration.requiredFilterKeys}
                rollupText={rollupText}
                setIsSaveDisabled={setIsSaveDisabled}
                showMeasureFilters={viewConfiguration.showMeasureFilters}
                showPrecisionConfig={viewConfiguration.showPrecisionConfig}
                showRecordLimit={viewConfiguration.showRecordLimit}
                showTimeAndPrecision={viewConfiguration.showTimeAndPrecision}
                timeAndPrecisionTypeOptions={
                    viewConfiguration.timeAndPrecisionTypeOptions
                }
                timeAndPrecisionRangeDomains={
                    viewConfiguration.timeAndPrecisionRangeDomains
                }
                timeAndPrecision={state.dashboardTimeAndPrecision}
                whereClauses={state.dashboardWhereClauses}
                whereClauseOptions={viewConfiguration.whereClauseOptions}
            >
                <SavedDashboardContainer
                    dashboardKey={props.dashboardKey}
                    isSaveDisabled={isSaveDisabled}
                    isSavedFilterModified={isSavedFilterModified}
                    loadSavedDashboard={props.loadSavedDashboard}
                    onSave={onSaveDashboard}
                    pageTitle={viewConfiguration.title}
                    savedDashboardNames={props.savedDashboardNames}
                    selectedValue={props.viewConfiguration}
                    siteMapKey={viewConfiguration.siteMapKey}
                    viewKey={state.viewKey}
                />
            </PageFilters>
        );
    }

    function renderShareModal() {
        const configurationForShare = {
            timeAndPrecision: state.dashboardTimeAndPrecision,
            filterValues: state.dashboardFilterValues,
            whereClauses: state.dashboardWhereClauses,
            recordLimit: state.dashboardRecordLimit.recordLimit,
            isDescending: !!state.dashboardRecordLimit.isDescending,
        };

        const unappliedFiltersExist = pageFiltersRef.current
            ? pageFiltersRef.current.unappliedFiltersExist()
            : false;

        return (
            isShareModalVisible && (
                <ShareModal
                    viewKey={state.viewKey}
                    configurationForShare={configurationForShare}
                    dashboardKey={props.dashboardKey}
                    onDismiss={() => setIsShareModalVisible(false)}
                    unappliedFiltersExist={unappliedFiltersExist}
                />
            )
        );
    }

    function renderEmailModal() {
        return (
            emailState.isEmailModalVisible && (
                <EmailModal
                    onDismiss={() =>
                        setEmailState((prevState) => ({
                            ...prevState,
                            isEmailModalVisible: false,
                        }))
                    }
                    onSend={sendEmail}
                />
            )
        );
    }

    function renderManageSavedDashboards() {
        return (
            isManageSavedVisible && (
                <ManageSavedDashboardsModal
                    deleteSavedDashboard={props.deleteSavedDashboard}
                    onDismiss={() => setIsManageSavedVisible(false)}
                    pageTitle={viewConfiguration.title}
                    savedDashboardError={props.savedDashboardError}
                    savedDashboardNames={props.savedDashboardNames}
                    siteMapKey={viewConfiguration.siteMapKey}
                    viewKey={state.viewKey}
                />
            )
        );
    }

    function renderSavedDashboardError() {
        if (props.savedDashboardError) {
            return <ErrorAlert>{props.savedDashboardError}</ErrorAlert>;
        }

        return (
            <FadeErrorAlert showError={emailState.errors.length > 0}>
                {emailState.errors?.[0]?.name}
            </FadeErrorAlert>
        );
    }

    function getPrintMenuDescription(enabledExportTypes, exportType) {
        if (enabledExportTypes.length === 1) {
            return `${dashboardLabels.exportPreview}`;
        }

        return `${dashboardLabels.exportPreview} (${
            dashboardLabels[exportType.orientation]
        })`;
    }

    function getPageActions(showDashboardFilter, isRequiredFilters = false) {
        const pageActions = [];

        if (viewConfiguration.canManualRefresh) {
            const description = dashboardLabels.refresh;
            pageActions.push({
                description,
                iconClass: icons.refresh,
                sortOrder: 0,
                onClick: () => {
                    callDataForWidgets({
                        widgetIds: state.widgets.map((x) => x.id),
                        resetAllData: true,
                    });
                },
                isDisabled: isRequiredFilters,
            });
        }

        if (viewConfiguration.showShareAction) {
            pageActions.push({
                description: dashboardLabels.shareDashboard,
                iconClass: icons.share,
                sortOrder: 2,
                onClick: () => {
                    setIsShareModalVisible(true);
                },
                isDisabled: isRequiredFilters,
            });
        }

        if (
            viewConfiguration.features?.showPortraitPrint === true ||
            viewConfiguration.features?.showLandscapePrint === true
        ) {
            const enabledExportTypes = [];

            if (viewConfiguration.features?.showPortraitPrint === true) {
                enabledExportTypes.push(dashboardExportTypes[0]);
            }

            if (viewConfiguration.features?.showLandscapePrint === true) {
                enabledExportTypes.push(dashboardExportTypes[1]);
            }

            pageActions.push({
                description: dashboardLabels.downloadDashboard,
                iconClass: icons.download,
                sortOrder: 3,
                onClick: () => {},
                subActions: enabledExportTypes.map((exportType, index) => ({
                    description: getPrintMenuDescription(
                        enabledExportTypes,
                        exportType
                    ),
                    iconClass: exportType.icon,
                    sortOrder: index,
                    onClick: () => {
                        onDownload(exportType);
                    },
                    isDisabled: isRequiredFilters,
                })),
                isDisabled: isRequiredFilters,
            });
        }

        if (viewConfiguration.canEmail) {
            pageActions.push({
                description: dashboardLabels.emailPdf,
                iconClass: icons.subscriptions,
                sortOrder: 4,
                onClick: () => {
                    setEmailState((prevState) => ({
                        ...prevState,
                        isEmailModalVisible: true,
                    }));
                },
                isDisabled: isRequiredFilters,
            });
        }

        if (showDashboardFilter) {
            pageActions.push({
                description: dashboardLabels.manageSavedFilters,
                iconClass: icons.cog,
                sortOrder: 5,
                onClick: () => {
                    setIsManageSavedVisible(true);
                },
            });
        }

        pageActions.push({
            description: dashboardLabels.changeResponsiveLayout,
            iconClass: icons.mosaic,
            sortOrder: 6,
            onClick: () => {},
            subActions: Object.values(responsiveLayouts).map(
                (responsiveLayout, index) => ({
                    description: dashboardLabels[responsiveLayout],
                    iconClass:
                        responsiveLayout === selectedResponsiveLayout
                            ? icons.check
                            : 'menu-item-icon',
                    sortOrder: index,
                    onClick: () => {
                        setSelectedResponsiveLayout(responsiveLayout);
                    },
                })
            ),
        });

        return pageActions;
    }

    const showDashboardFilter = shouldShowDashboardFilter(
        viewConfiguration.showTimeAndPrecision,
        viewConfiguration.availableFilters,
        viewConfiguration.requiredFilterKeys,
        selectOptions
    );

    function onViewChange(key) {
        props.changeView(key, {
            ...viewConfiguration,
            filterValues: state.dashboardFilterValues,
            recordLimit: state.dashboardRecordLimit.recordLimit,
            isDescending: !!state.dashboardRecordLimit.isDescending,
            savedMetricData: state.metricData,
            timeAndPrecision: state.dashboardTimeAndPrecision,
            whereClauses: state.dashboardWhereClauses,
        });
    }

    function addDesktopModifierClass(baseClass) {
        if (
            selectedResponsiveLayout === responsiveLayouts.desktop ||
            (selectedResponsiveLayout === responsiveLayouts.auto &&
                width >= minWindowWidths.desktop)
        ) {
            return `${baseClass} ${baseClass}--desktop`;
        }

        return baseClass;
    }

    function renderToast() {
        return (
            <ToastContainer>
                <Toast
                    notificationMessage={`${downloadsInProgress} ${exportLabels.downloadInProgress}`}
                    isMounted={downloadsInProgress > 0}
                    onDismiss={() => setDownloadsInProgress(0)}
                />
                <Toast
                    notificationMessage={`${emailState.numberOfEmails} ${exportLabels.emailInProgress}`}
                    isMounted={
                        emailState.showEmailToast &&
                        emailState.numberOfEmails > 0
                    }
                    onDismiss={() =>
                        setEmailState((prevState) => ({
                            ...prevState,
                            showEmailToast: false,
                        }))
                    }
                />
            </ToastContainer>
        );
    }

    if (
        state.isViewStateLoading ||
        !isRequiredFiltersHandled(
            viewConfiguration.requiredFilterKeys,
            state.dashboardFilterValues,
            viewConfiguration.availableFilters,
            selectOptions
        ) ||
        !areTimeAndPrecisionFieldsValid(
            state.dashboardTimeAndPrecision,
            viewConfiguration.timeAndPrecisionRangeDomains,
            viewConfiguration.showPrecisionConfig,
            interpolateString,
            dashboardLabels
        )
    ) {
        return (
            <div
                className="dashboard"
                style={viewConfiguration.containerStyles}
            >
                <DashboardHeader
                    onViewChange={onViewChange}
                    onlineHelpId={viewConfiguration.onlineHelpId}
                    pageActions={getPageActions(showDashboardFilter, true)}
                    setTogglesDisabled={() => setIsViewStateLoading(dispatch)}
                    showSsiWarning={viewConfiguration.showSsiWarning}
                    subtitle={viewConfiguration.subtitle}
                    title={viewConfiguration.title}
                    togglesDisabled={state.isViewStateLoading}
                    viewKey={state.viewKey}
                    viewOptions={props.viewOptions}
                />
                {renderSavedDashboardError()}
                {renderPageFilters(true)}
                {renderManageSavedDashboards()}
            </div>
        );
    }

    return (
        <div className="dashboard" style={viewConfiguration.containerStyles}>
            <DashboardHeader
                onViewChange={onViewChange}
                isMetricDataLoading={state.isMetricDataLoading}
                onlineHelpId={viewConfiguration.onlineHelpId}
                pageActions={getPageActions(showDashboardFilter)}
                setTogglesDisabled={() => setIsViewStateLoading(dispatch)}
                showSsiWarning={viewConfiguration.showSsiWarning}
                subtitle={viewConfiguration.subtitle}
                title={viewConfiguration.title}
                togglesDisabled={state.isViewStateLoading}
                viewKey={state.viewKey}
                viewOptions={props.viewOptions}
            />
            {viewConfiguration.showSsiWarning && <SsiHeader />}
            {renderSavedDashboardError()}
            {showDashboardFilter && renderPageFilters()}
            <div className={addDesktopModifierClass('dashboard-widgets')}>
                <div
                    className={addDesktopModifierClass('dashboard-widget-grid')}
                    style={getGridTemplateStyles(
                        viewConfiguration.numberOfGridRows
                    )}
                >
                    {renderPanels(viewConfiguration.panels, state.widgets)}
                </div>
            </div>
            {viewConfiguration.showSsiWarning && <SsiFooter />}
            {renderManageSavedDashboards()}
            {renderShareModal()}
            {renderEmailModal()}
            {renderToast()}
        </div>
    );
}
/* eslint-enable max-statements, complexity */

Dashboard.propTypes = {
    changeView: PropTypes.func.isRequired,
    dashboardKey: PropTypes.string.isRequired,
    deleteSavedDashboard: PropTypes.func,
    loadSavedDashboard: PropTypes.func,
    saveDashboard: PropTypes.func,
    savedDashboardError: PropTypes.string,
    savedDashboardNames: PropTypes.instanceOf(Array),
    siteLanguage: PropTypes.string,
    viewConfiguration: PropTypes.instanceOf(Object).isRequired,
    viewOptions: PropTypes.instanceOf(Array).isRequired,
};

Dashboard.defaultProps = {
    deleteSavedDashboard: () => null,
    loadSavedDashboard: () => null,
    saveDashboard: () => null,
    savedDashboardError: null,
    savedDashboardNames: [],
    siteLanguage: 'en',
};
